import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Form, Tabs, Collapse, Grid, TabsProps, Badge } from 'antd'
import { useSelector } from 'react-redux'
import { Files } from './tab-files'
import { Timeline } from './tab-timeline'
import { StagePlan } from '../stage-plan'
import { getTimeLineData, getTimeLineFiles } from '../../../../../store/timeline/action-creators'
import { getStagePlanData } from '../../../../../store/stagePlan/action-creators'
import AppChat from '../../../../../components/app-chat'
import { uploadChatFiles } from '../../../../../components/app-chat/utils'
import { createSession } from '../../../../claims-request/card/selectors'
import fetchAPI from '../../../../../../lib/utils/fetch-api'
import { useAppDispatch } from '../../../../../hooks/useAppDispatch/useAppDispatch'
import { StyledCollapse } from '../../../../../components/v2/field-group/styles'
import { TimelineTab } from '../../styles'
import { Forms } from '../forms'
import EntityRelations from '../../../../../components/entity-relations'

interface CardTabsProps {
  onChange?: (key: string) => void
  onTabClick?: (key: string) => void
  id: string | number
}

const CardTabs: React.FC<CardTabsProps> = memo((props: CardTabsProps) => {
  const { onChange, onTabClick, id } = props
  const dispatch = useAppDispatch()
  const screens = Grid.useBreakpoint()
  const isMobile = !screens?.md
  const [activeKey, setActiveKey] = useState<string>('data') // data | chat
  const [timelineLoaded, setTimelineLoaded] = useState<boolean>(false)
  const [fullTimelineLoaded, setFullTimelineLoaded] = useState<boolean>(false)
  const [chatData, setChatData] = useState<any>([])
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [formChat] = Form.useForm()
  const { sessionData } = useSelector(createSession)

  const getChatData = useCallback(async () => {
    if (!id || id === 'new') {
      return
    }
    const resultChat = await fetchAPI(`/api/claims_request_chat/?filter[claims_request_id]=${id}`)
    setUnreadCount(
      (resultChat || [])?.filter((item: any) => !item.is_read && !item.manager_id)?.length || 0,
    )
    setChatData(resultChat || [])
  }, [id])

  useEffect(() => {
    if (id) {
      getChatData()
    }
  }, [id, getChatData])

  const handleOnChange = useCallback(
    (key: string) => {
      setActiveKey(key)
      if (key === 'timeline' && !fullTimelineLoaded) {
        dispatch(getTimeLineData('claim', id))
        dispatch(getStagePlanData('claim', id))
        setTimelineLoaded(true)
        setFullTimelineLoaded(true)
      } else if (key === 'files' && !timelineLoaded) {
        dispatch(getTimeLineFiles('claim', id))
        setTimelineLoaded(true)
      }
      if (key === 'chat') {
        getChatData()
      }
      if (onChange) {
        onChange(key)
      }
    },
    [dispatch, id, timelineLoaded, fullTimelineLoaded, onChange, getChatData],
  )

  const handleOnTabClick = useCallback(
    (key: string) => {
      if (onTabClick) {
        onTabClick(key)
      }
    },
    [onTabClick],
  )

  const userId = sessionData?.user?.id

  const onChatSend = useCallback(
    async (values: any) => {
      const uploadedFiles = await uploadChatFiles(values.files || [])

      await fetchAPI(`/api/claims_request_chat/`, {
        method: 'POST',
        body: JSON.stringify({
          claims_request_id: id,
          manager_id: userId,
          msg: values.message,
          files: uploadedFiles.length ? JSON.stringify(uploadedFiles) : null,
        }),
      })

      getChatData()
    },
    [id, userId, getChatData],
  )

  const handleMessageRead = useCallback(async (messageId: number) => {
    try {
      await fetchAPI(`/api/claims_request_chat/${messageId}`, { method: 'PATCH' })
      setChatData(
        (prev: any[]) =>
          prev?.map(item => (item.id === messageId ? { ...item, is_read: true } : item)) || [],
      )
      setUnreadCount(prev => Math.max(prev - 1, 0))
    } catch (error) {
      console.error('Не удалось отметить сообщение прочитанным', error)
    }
  }, [])

  const tabItems = useMemo<TabsProps['items']>(() => {
    const items: TabsProps['items'] = [
      {
        label: 'Данные рекламации',
        key: 'data',
        children: <Forms id={id} />,
      },
      {
        label: 'Лента событий',
        key: 'timeline',
        destroyOnHidden: true,
        children: isMobile ? (
          <>
            <StyledCollapse ghost>
              <Collapse.Panel header={'Прогноз исполнения'} key={'stage-plan-panel'}>
                <StagePlan id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
            <StyledCollapse defaultActiveKey={['timeline-panel']} ghost>
              <Collapse.Panel header={'Лента событий'} key={'timeline-panel'}>
                <Timeline id={id} hideHeader />
              </Collapse.Panel>
            </StyledCollapse>
          </>
        ) : (
          <TimelineTab>
            <StagePlan id={id} />
            <Timeline id={id} />
          </TimelineTab>
        ),
      },
      {
        label: 'Прикрепленные файлы',
        key: 'files',
        children: <Files id={id} />,
      },
      ...(id !== 'new'
        ? [
            {
              label:
                unreadCount > 0 ? (
                  <Badge
                    count={unreadCount}
                    size="small"
                    offset={[6, 0]}
                    color="rgb(130, 30, 130)"
                    title={`Непрочитанных сообщений в чате: ${unreadCount}`}
                  >
                    Обсуждения по заявке
                  </Badge>
                ) : (
                  'Обсуждения по заявке'
                ),
              key: 'chat',
              children: (
                <AppChat
                  data={chatData}
                  form={formChat}
                  onSubmit={onChatSend}
                  onMessageRead={handleMessageRead}
                />
              ),
            } as any,
          ]
        : []),
      {
        key: 'relations',
        label: 'Связи',
        children: <EntityRelations entityType="claim" entityId={id} />,
      },
    ]
    return items
  }, [isMobile, id, chatData, unreadCount, formChat, onChatSend, handleMessageRead])

  return (
    <Tabs
      onChange={handleOnChange}
      onTabClick={handleOnTabClick}
      activeKey={activeKey}
      items={tabItems}
    />
  )
})

CardTabs.displayName = 'CardTabs'
export default CardTabs
